import React from "react";
import { useNavigate } from "react-router";
import { Button } from "@accessdev/arc-ui-react";
import { Creators } from "@ReduxStore/actionTypes";
import { selectors } from "@ReduxStore/reducers";
import { useSelector, useDispatch } from "react-redux";
import { Member, AuthModalTypes, ROUTES } from "@Src/constants";
import { Trans } from "@lingui/react/macro";

/**
 * useSigninButton hook
 * Provides a SignInButton and SignUpButton component, along with click handlers to open the auth modal.
 * Also provides a boolean isSsoRegType to determine if the registration type is SSO.
 * @returns:
 *  @Component - SignInButton: Component that opens the sign in modal when clicked. Can pass all the same props as an ARC Button component.
 *  @Component - SignUpButton: Component that opens the sign up modal when clicked. Can pass all the same props as an ARC Button component. Will not render if registration type is SSO.
 *  @Function - handleSigninClick: Function to open the sign in modal.
 *  @Function - handleSignupClick: Function to open the sign up modal. Will not open if registration type is SSO.
 *  @Boolean - isSsoRegType: Boolean to determine if the registration type is SSO.
 */
function useSigninButton() {
  const registrationType = useSelector(selectors.getRegistrationType);
  const member = useSelector(selectors.getMember);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isSsoRegType = registrationType === Member.DEALS_SSO;
  const isPseudoSignedIn = member.has_password || member.has_registered;

  const handleSigninClick = async () => {
    if (!isSsoRegType) {
      dispatch(Creators.setAuthOpen(true, AuthModalTypes.SIGN_IN));
    } else {
      navigate(ROUTES.REDIRECT_SSO);
    }
  };

  const handleSignupClick = () => {
    if (!isSsoRegType) {
      dispatch(Creators.setAuthOpen(true, AuthModalTypes.SIGN_UP));
    }
  };

  const SignInButton = (props) => {
    return (
      <Button {...props} onClick={handleSigninClick}>
        <Trans>Sign In</Trans>
      </Button>
    );
  };

  const SignUpButton = (props) => {
    if (isSsoRegType || isPseudoSignedIn) return null;
    return (
      <Button {...props} onClick={handleSignupClick}>
        <Trans>Sign Up</Trans>
      </Button>
    );
  };

  return {
    SignInButton,
    SignUpButton,
    handleSigninClick,
    handleSignupClick,
    isSsoRegType,
    isPseudoSignedIn,
  };
}

export default useSigninButton;
